/**
 * Miniaturas das fotos de evidência, com ampliação ao tocar.
 *
 * A foto é protegida por JWT: vem por fetch e vira object URL (não dá para usar
 * `<img src="/api/...">`, o token vive no localStorage).
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../lib/api";
import { IconX } from "../../components/icons";

export interface EvidenceLike {
  id: string;
  fileName: string;
  mimeType: string;
  caption?: string | null;
  origin?: "answer" | "non_conformity";
}

function useBlobUrl(id: string) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    let criada: string | null = null;
    api.blobUrl(`/maintenance/attachments/${id}`)
      .then((u) => { criada = u; if (vivo) setUrl(u); else URL.revokeObjectURL(u); })
      .catch(() => undefined);
    return () => { vivo = false; if (criada) URL.revokeObjectURL(criada); };
  }, [id]);
  return url;
}

export function EvidenceThumb({ anexo, size = "md" }: { anexo: EvidenceLike; size?: "sm" | "md" }) {
  const url = useBlobUrl(anexo.id);
  const [aberta, setAberta] = useState(false);
  const lado = size === "sm" ? "w-14 h-14" : "w-20 h-20";
  const legenda = anexo.caption ?? (anexo.origin === "non_conformity" ? "Foto da ocorrência" : anexo.origin === "answer" ? "Foto do checklist" : anexo.fileName);

  if (!anexo.mimeType.startsWith("image/")) {
    return <span className="text-xs text-gray-500">{anexo.fileName}</span>;
  }
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAberta(true); }}
        className={`${lado} rounded-lg border border-surface-border overflow-hidden bg-surface-muted flex-shrink-0`}
        title={legenda}
      >
        {url ? <img src={url} alt={legenda} className="w-full h-full object-cover" /> : <div className="w-full h-full animate-pulse" />}
      </button>
      {aberta && url && createPortal(
        <div className="fixed inset-0 z-50 bg-gray-900/80 flex flex-col items-center justify-center p-4" onClick={() => setAberta(false)} role="dialog" aria-modal="true">
          <button className="absolute top-4 right-4 text-white" aria-label="Fechar"><IconX className="w-6 h-6" /></button>
          <img src={url} alt={legenda} className="max-w-full max-h-[85vh] rounded-lg object-contain" />
          <p className="mt-2 text-sm text-white/80">{legenda}</p>
        </div>,
        document.body,
      )}
    </>
  );
}

export function EvidenceThumbs({ anexos, size }: { anexos?: EvidenceLike[] | null; size?: "sm" | "md" }) {
  if (!anexos?.length) return null;
  return (
    <div className="mt-2 flex gap-2 flex-wrap">
      {anexos.map((a) => <EvidenceThumb key={a.id} anexo={a} size={size} />)}
    </div>
  );
}
